/* eslint-disable @next/next/no-img-element */
"use client";
import { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import MenuIcon from "@/icons/menu.svg";
import CrossIcon from "@/icons/cross.svg";
import AngleDown from "@/icons/angleDown.svg";
import SearchIcon from "@/icons/search-icon.svg";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getTranslation } from "@/i18n/get-translation";
import Facebook from "@/icons/facebook-dark.svg";
import Linkedin from "@/icons/linkedin-dark.svg";
import Instagram from "@/icons/instagram-dark.svg";
import Image from "next/image";
import LucyWhite from "@/icons/lucy-white.svg";
import Close from "@/icons/close.svg";
import { convertUrlString } from "@/utils/convert-url-string";
import SubCategories from "@components/sub-categories/sub-categories";

interface INavigation {
  name: string;
  href: string;
}
interface IBlogsHeader {
  locale: string;
}
export default function SubHeader({ locale }: IBlogsHeader) {
  const t = getTranslation(locale, "blogsHeader");
  const tSubCat = getTranslation(locale, "blogsCategories");
  const [toggle, setToggle] = useState(false);
  const [isSearchOpen, setisSearchOpen] = useState(false);
  const [isSearchOpenMobile, setisSearchOpenMobile] = useState(false);
  const pathname = usePathname();
  const toggleSearch = () => {
    setisSearchOpen(!isSearchOpen);
  };
  const toggleSearchMobile = () => {
    setisSearchOpenMobile(!isSearchOpenMobile);
  };

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      window.scrollY > 10 ? setIsScrolled(true) : setIsScrolled(false);
    });
  });

  const renderActiveLanguage = () => {
    switch (pathname.substring(0, 3)) {
      case "/en":
        return t("en");
      case "/de":
        return t("de");
      case "/fr":
        return t("fr");
    }
  };

  const subCategoreis: INavigation[] = [
    { name: "blog", href: `/${locale}/blog` },
    { name: "news", href: `/${locale}/news` },
    { name: "contact", href: `/${locale}/contact-us` },
  ];

  return (
    <header
      className={`inset-x-0 z-40 text-xl fixed top-0 left-0 w-full transition-all ${
        isScrolled ? "text-primary-800" : "text-white"
      } max-md:text-primary-800 ${isScrolled && "bg-white shadow-200"}`}
    >
      <nav
        className="w-full flex justify-between items-center navbar p-4 px-20 max-md:px-7 relative z-40"
        aria-label="Global"
      >
        {/* Social Icons */}
        <div className="text-lg text-primary-800 md:flex hidden items-center gap-[1.25rem] ">
          <a
            href="https://www.facebook.com/medlucyofficial"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Facebook"
            className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
          >
            <Facebook />
          </a>
          <a
            href="https://www.instagram.com/medlucyofficial/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Instagram"
            className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
          >
            <Instagram />
          </a>
          <a
            href="https://www.linkedin.com/company/medlucy/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on LinkedIn"
            className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
          >
            <Linkedin />
          </a>
        </div>

        {/*  Logo */}
        <div className="flex md:absolute static left-1/2 md:-translate-x-1/2 top-1/2 md:-translate-y-1/2">
          <Link href="/">
            <img
              src="/icons/lucy-blog.svg"
              alt="Lucy Blog"
              className="w-[6.2rem]"
            />
          </Link>
        </div>

        {/* Right Nav */}
        <div className="text-primary-800 flex items-center md:ml-0 ml-auto gap-[1rem]">
          {/* desktop Search */}
          <div className="relative lg:w-[18rem] w-[14rem] md:flex hidden items-center ">
            <button
              onClick={toggleSearch}
              className={`rounded-full p-0 md:flex hidden z-40 absolute right-[0.8rem] lg:top-[0.72rem] top-[0.55rem] leading-4 hover:text-primary-700`}
            >
              <SearchIcon className="w-6" />
            </button>
            <div
              className={`${
                isSearchOpen
                  ? "opacity-100 lg:w-[18rem] w-[14rem]"
                  : "opacity-0 w-[0rem]"
              } flex relative items-center transition-all duration-500 transform`}
            >
              <input
                type="text"
                className="w-full pl-3 pr-12 lg:py-[0.7rem] py-[0.55rem] text-base bg-white outline-0 focus:border-primary-900 border rounded-full border-[#D6C8FF] 
                "
                placeholder={`${t("search")}...`}
              />
            </div>
          </div>

          {/* language Selection */}
          <div className="relative">
            <Menu>
              <Menu.Button className="flex items-center text-inherit hover:opacity-80 aria-expanded:opacity-80 w-12 h-12 justify-center">
                {renderActiveLanguage()}
                <AngleDown className="mt-1 ml-2 w-[0.875rem]" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 py-1 px-3 bg-white rounded text-primary-900 border-2 border-primary-400 flex flex-col">
                {pathname.substring(0, 3) !== "/en" && (
                  <Menu.Item>
                    <Link
                      className="w-12 h-12 justify-center flex items-center"
                      href={`/en-lu/${
                        pathname.includes("blog") ? "blog" : "news"
                      }`}
                    >
                      {t("en")}
                    </Link>
                  </Menu.Item>
                )}
                {pathname.substring(0, 3) !== "/de" && (
                  <Menu.Item>
                    <Link
                      className="w-12 h-12 justify-center flex items-center"
                      href={`/de-lu/${
                        pathname.includes("blog") ? "blog" : "news"
                      }`}
                    >
                      {t("de")}
                    </Link>
                  </Menu.Item>
                )}
                {pathname.substring(0, 3) !== "/fr" && (
                  <Menu.Item>
                    <Link
                      className="w-12 h-12 justify-center flex items-center"
                      href={`/fr-lu/${
                        pathname.includes("blog") ? "blog" : "news"
                      }`}
                    >
                      {t("fr")}
                    </Link>
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex items-center">
          <button
            className="ml-4 w-12 h-12 flex items-center justify-center"
            onClick={() => setToggle(!toggle)}
          >
            {toggle ? <CrossIcon /> : <MenuIcon />}
          </button>

          {/* Sidebar */}
          <div
            className={`${
              !toggle ? "hidden" : "flex"
            } p-4 px-7 bg-primary-500 absolute z-50 border-b flex-col gap-4 top-[5.5rem] left-0 right-0 m-0`}
          >
            <div className="flex justify-between items-center w-full">
              <div
                className={`text-primary-800 flex  items-center gap-[1.3rem] `}
              >
                <a
                  href="https://www.facebook.com/medlucyofficial"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
                >
                  <Facebook />
                </a>
                <a
                  href="https://www.instagram.com/medlucyofficial/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
                >
                  <Instagram />
                </a>
                <a
                  href="https://www.linkedin.com/company/medlucy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on LinkedIn"
                  className=" flex items-center justify-center leading-[0] bg-white/25 rounded-full"
                >
                  <Linkedin />
                </a>
              </div>
              <button
                onClick={toggleSearchMobile}
                className={`rounded-full p-0 leading-4 hover:text-primary-700`}
              >
                <SearchIcon />
              </button>
            </div>
            {/* searchbar */}
            <div
              className={`items-center w-full transition-all px-2 bg-white rounded-lg border border-primary-400 focus:outline-1 ${
                isSearchOpenMobile ? "flex" : "hidden"
              }`}
            >
              <input
                type="text"
                className="w-full px-2 py-2 focus:outline-none border-none bg-transparent"
                placeholder={`${t("search")}...`}
              />
              <button
                onClick={toggleSearchMobile}
                className="text-primary-800 p-0 hover:text-primary-700 h-full"
              >
                <CrossIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`w-full z-50 absolute top-0 left-0 bg-white/70 h-screen ${
            toggle ? "block" : "hidden"
          }`}
        >
          <Image
            width={1728}
            height={2692}
            loading="lazy"
            className="absolute top-0 -right-[50%] z-10 w-[110%] max-w-none opacity-30 -translate-y-[20%]"
            src="/images/dna-white.webp"
            alt=""
          />
          <div className="absolute h-full -top-[53vw] max-sm:-top-[15vw] left-0 right-0">
            <Image
              width={375}
              height={630}
              loading="lazy"
              className="absolute top-0 right-0 w-full"
              src="/icons/menu-left.svg"
              alt=""
            />
            <Image
              width={375}
              height={687}
              loading="lazy"
              className="absolute top-0 right-0 w-full"
              src="/icons/menu-right.svg"
              alt=""
            />
          </div>

          <div
            className={`flex flex-col relative z-10 text-white text-xl px-7 py-4`}
          >
            <div className="mb-9 flex justify-between items-center">
              <Link href="/">
                <LucyWhite className="w-[6.563rem] h-[3.938rem] max-md:w-[5rem]" />
              </Link>
              <div className="relative ml-[2rem] max-md:ml-auto">
                <Menu>
                  <Menu.Button className="flex items-center text-inherit hover:opacity-80 aria-expanded:opacity-80 w-12 h-12 justify-center">
                    {renderActiveLanguage()}
                    <AngleDown className="mt-1 ml-2 w-[0.875rem]" />
                  </Menu.Button>
                  <Menu.Items className="absolute right-0 py-1 px-3 bg-white rounded text-primary-900 border-2 border-primary-400">
                    {pathname.substring(0, 3) !== "/en" && (
                      <Menu.Item>
                        <Link
                          className="w-12 h-12 justify-center flex items-center"
                          href={`/en-lu${convertUrlString(pathname)}`}
                        >
                          {t("en")}
                        </Link>
                      </Menu.Item>
                    )}
                    {pathname.substring(0, 3) !== "/de" && (
                      <Menu.Item>
                        <Link
                          className="w-12 h-12 justify-center flex items-center"
                          href={`/de-lu${convertUrlString(pathname)}`}
                        >
                          {t("de")}
                        </Link>
                      </Menu.Item>
                    )}
                    {pathname.substring(0, 3) !== "/fr" && (
                      <Menu.Item>
                        <Link
                          className="w-12 h-12 justify-center flex items-center"
                          href={`/fr-lu${convertUrlString(pathname)}`}
                        >
                          {t("fr")}
                        </Link>
                      </Menu.Item>
                    )}
                  </Menu.Items>
                </Menu>
              </div>
              <div className="hidden max-md:block">
                <button
                  className="ml-8 w-12 h-12 -mr-3 flex items-center justify-center"
                  onClick={() => setToggle(!toggle)}
                >
                  <Close />
                </button>
              </div>
            </div>
            <h4 className="text-[1.9rem]   font-medium mb-4 text-white">
              {t("menu")}
            </h4>
            {subCategoreis.map((item: INavigation) => (
              <Link
                key={item.name}
                href={`${item.href}`}
                className="mb-6 hover:opacity-80"
              >
                {tSubCat(item.name)}
              </Link>
            ))}
          </div>
        </div>
      </nav>
      <div
        className={` md:hidden bg-primary-500 px-7 py-3 ${
          isScrolled ? "hidden" : "flex"
        }`}
      >
        <div className={`w-full transition-all relative`}>
          <input
            type="input"
            className="w-full pl-4 pr-10 py-3 text-base focus:outline-none border-none bg-white rounded-full"
            placeholder={`${t("search")}...`}
          />
          <button className="text-primary-800 p-0 hover:text-primary-700 h-full absolute right-[0.9rem] top-0">
            <SearchIcon className="w-5" />
          </button>
        </div>
      </div>
    </header>
  );
}
